/**
 * Position Header Component
 *
 * Custom header for PositionDetail screen displaying symbol and instrument name.
 * Used in navigation header via PortfolioNavigator configuration.
 */

import React, { useMemo, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPosition } from '../../services/portfolioApi';
import { getLocalizedInstrumentName } from '../../utils/translationHelpers';

interface PositionHeaderProps {
  positionId: number;
}

export function PositionHeader({ positionId }: PositionHeaderProps) {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [position, setPosition] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosition = async () => {
      try {
        const data = await getPosition(positionId);
        setPosition(data);
      } catch (error) {
        console.error('Failed to fetch position for header:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosition();
  }, [positionId]);

  if (loading) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  if (!position) {
    return (
      <View style={styles.container}>
        <Text style={[styles.symbol, { color: theme.colors.text }]}>{positionId}</Text>
      </View>
    );
  }

  const localizedName = getLocalizedInstrumentName(
    position.instrument_name,
    position.instrument_name_sch,
    position.instrument_name_tch,
    language
  );

  return (
    <View style={styles.container}>
      <Text style={[styles.symbol, { color: theme.colors.text }]} numberOfLines={1}>
        {position.symbol}
      </Text>
      {localizedName && (
        <Text
          style={[styles.instrumentName, { color: theme.colors.textSecondary }]}
          numberOfLines={1}
        >
          {localizedName}
        </Text>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flexDirection: 'column',
      maxWidth: 250,
    },
    symbol: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    instrumentName: {
      fontSize: theme.typography.fontSizeSm,
    },
  });
}
